import React, { useCallback, useMemo, useState } from "react"
import {
  Box,
  Typography,
  Button,
  Tab,
  useTheme,
  Slide,
  SlideProps,
} from "@mui/material"
import { BaseModal } from "components/modal"
import { getPostDetail, IPostDetail } from "utils/feature"
import { useTranslation } from "providers/i18n"
import { ScrollableTab } from "components/ScrollableTab"
import { useNavigate } from "react-router-dom"
import { Image } from "components/Image"
import { pushDataLayer } from "utils/googletagmanager"
import { GTM_POST_LOCATION, GTM_VIEW_POST } from "constant"
import { get } from "lodash"

const ModalTransition = React.forwardRef<HTMLElement, SlideProps>(
  function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />
  }
)

const AttractionModal = ({
  data: attractions = [],
  sx = {},
  onClose,
  open = true,
  ...props
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const {
    root,
    contentContainer,
    image,
    informationWrapper,
    textContentWrapper,
    titleText,
    descriptionText,
    buttonContainer,
    locationButton,
    locationWrapper,
    tabContainer,
    tabLabelContainer,
    locationNameText,
    levelText,
    tabItem,
  } = theme.components?.[`AttractionModal`].styleOverrides || {}
  const { scrollableTabProps = {} } =
    theme.components?.[`AttractionModal`].defaultProps || {}

  const [activeTabIndex, setActiveTabIndex] = useState(0)

  const transformedAttractions = useMemo(
    () => attractions?.map(getPostDetail),
    [attractions]
  )

  const activeAttraction = useMemo(
    () => (transformedAttractions[activeTabIndex] || {}) as IPostDetail,
    [transformedAttractions, activeTabIndex]
  )

  const handleChange = useCallback(
    (event, newValue) => {
      setActiveTabIndex(newValue)
      pushDataLayer({
        event: GTM_VIEW_POST,
        post_type: get(transformedAttractions[newValue], "type"),
        post_category: get(transformedAttractions[newValue], "category.en"),
        post_id: get(transformedAttractions[newValue], "id"),
        post_title: get(transformedAttractions[newValue], "title.en"),
      })
    },
    [transformedAttractions]
  )

  const handleClick = useCallback(() => {
    const locationUrl = get(activeAttraction, "locationUrl")
    navigate(locationUrl)
    pushDataLayer({
      event: GTM_POST_LOCATION,
      post_id: get(activeAttraction, "id"),
      post_location_name: get(activeAttraction.location, "en"),
      post_type: get(activeAttraction, "type"),
      post_category: get(activeAttraction, "category.en"),
      post_title: get(activeAttraction, "title.en"),
      post_location_url: locationUrl,
    })
  }, [navigate, activeAttraction])

  const hasLocation = activeAttraction?.hasLocation

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      sx={sx}
      {...props}
      TransitionComponent={ModalTransition}
    >
      <Box sx={root}>
        <Box sx={contentContainer}>
          <Image src={activeAttraction?.coverImage} sx={image} />
          <Box sx={informationWrapper}>
            <Box sx={textContentWrapper}>
              <Typography sx={titleText}>
                {t(activeAttraction?.title)}
              </Typography>

              <Typography sx={descriptionText}>
                {t(activeAttraction?.description)}
              </Typography>
            </Box>

            {hasLocation && (
              <Box sx={buttonContainer}>
                <Button
                  variant="contained"
                  sx={locationButton}
                  onClick={handleClick}
                >
                  {t("View Location")}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={locationWrapper}>
          <ScrollableTab
            value={activeTabIndex}
            onChange={handleChange}
            sx={tabContainer}
            {...scrollableTabProps}
          >
            {transformedAttractions.map((attraction, index) => {
              const { level = "" } = attraction?.locationDetail || {}
              return (
                <Tab
                  key={index}
                  label={
                    <Box sx={tabLabelContainer}>
                      <Typography
                        className="attraction-name"
                        sx={locationNameText}
                      >
                        {t(attraction?.title)}
                      </Typography>

                      {level && (
                        <Typography
                          variant="caption"
                          className="level-text"
                          sx={levelText}
                        >
                          {t(level)}
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={tabItem}
                  value={index}
                />
              )
            })}
          </ScrollableTab>
        </Box>
      </Box>
    </BaseModal>
  )
}

export default AttractionModal
